import { NameColumn } from '@/components/NameColumn';
import type { Columns } from '@/components/table/types';
import type { Port } from '@/types';

/**
 * Builds column definitions for Port entity tables
 *
 * Returns columns for:
 * - port: Port number and protocol (bold purple) with service name below
 * - source: Parent asset key
 * - status: Port status
 *
 * The port column uses NameColumn component to display:
 * - Port number and protocol in bold purple text (e.g., "80/tcp")
 * - Service name below in smaller gray text (e.g., "https")
 *
 * @returns Column definitions for TanStack Table
 *
 * @example
 * const columns = buildPortColumns();
 * // Returns array with port, source, and status columns
 * // Each column has defensive null checks returning '-' for missing values
 */
export function buildPortColumns(): Columns<Port> {
  return [
    {
      id: 'port',
      label: 'Port',
      cell: (item: Port) => {
        if (!item?.port) {
          return '-';
        }
        const portDisplay = `${item.port}/${item.protocol || 'tcp'}`;
        const serviceDescription = item.service || undefined;
        return (
          <NameColumn name={portDisplay} description={serviceDescription} />
        );
      },
    },
    {
      id: 'source',
      label: 'Asset',
      cell: (item: Port) => {
        if (!item?.source) {
          return '-';
        }
        // Extract asset identifier from source key format: #asset#group#identifier
        const parts = item.source.split('#');
        if (parts.length >= 4) {
          return parts[3]; // Return the identifier part
        }
        return item.source;
      },
    },
    {
      id: 'status',
      label: 'Status',
      cell: (item: Port) => {
        if (!item?.status) {
          return '-';
        }
        // Map status codes to human-readable labels
        const statusMap: Record<string, string> = {
          A: 'Active',
          I: 'Inactive',
          P: 'Pending',
        };
        return statusMap[item.status] || item.status;
      },
    },
  ];
}
